import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Watch, Vibrate, Wifi, Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SmartwatchSection({ darkMode, practice }) {
  const [watchSettings, setWatchSettings] = useState({
    enabled: false,
    vibration_on_escalation: true,
    office_network_ssids: [],
    quick_response_options: ['On my way', '1 min', '2 min', '5 min', 'Busy']
  });
  const [newSsid, setNewSsid] = useState('');

  useEffect(() => {
    if (practice?.watch_settings) {
      setWatchSettings(prev => ({ ...prev, ...practice.watch_settings }));
    }
  }, [practice]);

  const saveSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/watch/settings`, newSettings);
    } catch {
      toast.error('Failed to update settings');
    }
  };

  return (
    <div className={`glass-panel rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50' : ''}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-xl font-heading font-bold flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            <Watch className="w-5 h-5 text-purple-600" />
            Smartwatch Integration
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Configure settings for Apple Watch and Wear OS devices
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            {watchSettings.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <button
            onClick={async () => {
              const ns = { ...watchSettings, enabled: !watchSettings.enabled };
              setWatchSettings(ns);
              await saveSettings(ns);
              toast.success(ns.enabled ? 'Smartwatch integration enabled' : 'Smartwatch integration disabled');
            }}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
              watchSettings.enabled ? 'bg-purple-500' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
              watchSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Vibration on Escalation */}
        <div className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
              <Vibrate className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            </div>
            <div>
              <h3 className={`font-medium ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Vibration on Escalation</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Watch vibrates when an alert escalates
              </p>
            </div>
          </div>
          <button
            onClick={async () => {
              const ns = { ...watchSettings, vibration_on_escalation: !watchSettings.vibration_on_escalation };
              setWatchSettings(ns);
              await saveSettings(ns);
              toast.success(ns.vibration_on_escalation ? 'Vibration enabled' : 'Vibration disabled');
            }}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
              watchSettings.vibration_on_escalation ? 'bg-amber-500' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
              watchSettings.vibration_on_escalation ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
        </div>

        {/* Office Network SSIDs */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-sky-900/50' : 'bg-dp-100'}`}>
              <Wifi className={`w-5 h-5 ${darkMode ? 'text-dp-400' : 'text-dp-600'}`} />
            </div>
            <div>
              <h3 className={`font-medium ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Office Network Detection</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                WiFi networks that indicate the watch is in the office
              </p>
            </div>
          </div>
          
          <div className="flex gap-2 mb-3">
            <Input
              value={newSsid}
              onChange={(e) => setNewSsid(e.target.value)}
              placeholder="Enter WiFi network name (SSID)"
              className={`flex-1 ${darkMode ? 'bg-slate-600 border-slate-500 text-slate-100' : ''}`}
            />
            <Button
              onClick={async () => {
                if (!newSsid.trim()) return;
                const updatedSsids = [...watchSettings.office_network_ssids, newSsid.trim()];
                const ns = { ...watchSettings, office_network_ssids: updatedSsids };
                setWatchSettings(ns);
                setNewSsid('');
                await saveSettings(ns);
                toast.success('Network added');
              }}
              className="bg-dp-600 hover:bg-dp-700"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          
          {watchSettings.office_network_ssids.length > 0 ? (
            <div className="space-y-2">
              {watchSettings.office_network_ssids.map((ssid, idx) => (
                <div key={idx} className={`flex items-center justify-between px-3 py-2 rounded-lg ${darkMode ? 'bg-slate-600' : 'bg-white'}`}>
                  <div className="flex items-center gap-2">
                    <Wifi className={`w-4 h-4 ${darkMode ? 'text-dp-400' : 'text-dp-600'}`} />
                    <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{ssid}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      const updatedSsids = watchSettings.office_network_ssids.filter((_, i) => i !== idx);
                      const ns = { ...watchSettings, office_network_ssids: updatedSsids };
                      setWatchSettings(ns);
                      await saveSettings(ns);
                      toast.success('Network removed');
                    }}
                    className={darkMode ? 'text-slate-400 hover:text-red-400' : 'text-slate-500 hover:text-red-600'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-sm text-center py-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              No networks configured (watches will work on any network)
            </p>
          )}
        </div>

        {/* Quick Response Options */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
              <Clock className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <h3 className={`font-medium ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Quick Responses</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Customizable responses available on watch
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {watchSettings.quick_response_options.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className={`text-xs font-mono w-5 text-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{idx + 1}</span>
                <input
                  type="text"
                  value={option}
                  maxLength={40}
                  data-testid={`quick-response-input-${idx}`}
                  onChange={(e) => {
                    const updated = [...watchSettings.quick_response_options];
                    updated[idx] = e.target.value;
                    setWatchSettings(prev => ({ ...prev, quick_response_options: updated }));
                  }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                    darkMode 
                      ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-500' 
                      : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                  } focus:outline-none focus:ring-2 focus:ring-dp-500/50`}
                  placeholder={`Response ${idx + 1}`}
                />
              </div>
            ))}
          </div>
          <button
            data-testid="save-quick-responses-btn"
            onClick={async () => {
              const filtered = watchSettings.quick_response_options.filter(o => o.trim() !== '');
              if (filtered.length === 0) {
                toast.error('At least one quick response is required');
                return;
              }
              const ns = { ...watchSettings, quick_response_options: filtered };
              try {
                await axios.put(`${API}/watch/settings`, ns);
                setWatchSettings(ns);
                toast.success('Quick responses saved');
              } catch {
                toast.error('Failed to save quick responses');
              }
            }}
            className={`mt-3 w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              darkMode ? 'bg-dp-600 hover:bg-dp-500 text-white' : 'bg-dp-600 hover:bg-dp-700 text-white'
            }`}
          >
            Save Quick Responses
          </button>
        </div>

        <div className={`text-center pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Supports Apple Watch and Wear OS devices
          </p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Native watch apps required for full functionality
          </p>
        </div>
      </div>
    </div>
  );
}
